import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Key, Plus, Download, Ban, RotateCcw, UserPlus, Loader2, Package } from "lucide-react";

export default function AdminAppSumo() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [keys, setKeys] = useState([]);
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTier, setFilterTier] = useState("");
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showAssign, setShowAssign] = useState(null);
  const [assignEmail, setAssignEmail] = useState("");
  const [genForm, setGenForm] = useState({ count: 100, plan_name: "", tier: "tier_1", product_limit: 2500, tag: "appsumo", expires_days: 180 });
  const [generating, setGenerating] = useState(false);

  const fetchData = useCallback(() => {
    const params = new URLSearchParams({ page: page.toString(), limit: "30" });
    if (filterStatus) params.set("status", filterStatus);
    if (filterTier) params.set("tier", filterTier);
    Promise.all([
      api.get("/appsumo/admin/stats"),
      api.get(`/appsumo/admin/keys?${params}`),
      api.get("/subscriptions/plans"),
    ]).then(([s, k, p]) => {
      setStats(s.data);
      setKeys(k.data.keys);
      setTotal(k.data.total);
      setPlans(p.data);
      if (!genForm.plan_name && p.data.length > 0) {
        setGenForm(f => ({...f, plan_name: p.data[0].name, product_limit: p.data[0].product_limit}));
      }
    }).catch(console.error).finally(() => setLoading(false));
  }, [page, filterStatus, filterTier]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/appsumo/admin/generate-keys", { ...genForm, count: parseInt(genForm.count), product_limit: parseInt(genForm.product_limit), expires_days: genForm.expires_days });
      toast.success(`${res.data.generated} keys gegenereerd (batch: ${res.data.batch_id})`);
      setShowGenerate(false);
      fetchData();
    } catch { toast.error(t("common.error")); }
    finally { setGenerating(false); }
  };

  const handleExport = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/appsumo/admin/keys/export?status=unused`;
    const token = localStorage.getItem("sp_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `appsumo_keys_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        toast.success(t("admin.csv_export") + " OK");
      }).catch(() => toast.error(t("common.error")));
  };

  const statusColor = (s) => ({
    unused: "bg-blue-500/20 text-blue-400 border-0",
    redeemed: "bg-emerald-500/20 text-emerald-400 border-0",
    revoked: "bg-red-500/20 text-red-400 border-0",
    expired: "bg-amber-500/20 text-amber-400 border-0",
  }[s] || "");

  const tierLabel = (tier) => ({
    tier_1: `Tier 1 (6 ${t("admin.months_label")})`,
    tier_2: `Tier 2 (12 ${t("admin.months_label")})`,
    tier_3: `Tier 3 (24 ${t("admin.months_label")})`,
    lifetime: "Lifetime",
  }[tier] || tier);

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-appsumo-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center"><Key className="w-5 h-5 text-orange-500" /></div>
          <div>
            <h1 className="text-2xl font-bold">{t("admin.appsumo_title")}</h1>
            <p className="text-sm text-muted-foreground">{t("admin.appsumo_subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-keys-btn"><Download className="w-3.5 h-3.5 mr-1.5" />{t("admin.csv_export")}</Button>
          <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="generate-keys-btn"><Plus className="w-4 h-4 mr-2" />{t("admin.generate_keys")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("admin.generate_keys_title")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>{t("admin.key_count")}</Label><Input type="number" value={genForm.count} onChange={e => setGenForm({...genForm, count: e.target.value})} className="mt-1" data-testid="gen-count" /></div>
                <div><Label>{t("admin.link_subscription")}</Label>
                  <Select value={genForm.plan_name} onValueChange={v => {
                    const plan = plans.find(p => p.name === v);
                    setGenForm({...genForm, plan_name: v, product_limit: plan?.product_limit || 2500});
                  }}>
                    <SelectTrigger className="mt-1" data-testid="gen-plan"><SelectValue placeholder={t("admin.choose_subscription")} /></SelectTrigger>
                    <SelectContent>
                      {plans.map(p => (
                        <SelectItem key={p.id || p.name} value={p.name}>{p.name} ({p.product_limit?.toLocaleString()} {t("admin.products_count").toLowerCase()})</SelectItem>
                      ))}
                      <SelectItem value="Lifetime Deal">Lifetime Deal (custom)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("admin.tier")}</Label>
                  <Select value={genForm.tier} onValueChange={v => {
                    const periods = { tier_1: 180, tier_2: 365, tier_3: 730, lifetime: null };
                    setGenForm({...genForm, tier: v, expires_days: periods[v]});
                  }}>
                    <SelectTrigger className="mt-1" data-testid="gen-tier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tier_1">Tier 1 (6 {t("admin.months_label")})</SelectItem>
                      <SelectItem value="tier_2">Tier 2 (12 {t("admin.months_label")})</SelectItem>
                      <SelectItem value="tier_3">Tier 3 (24 {t("admin.months_label")})</SelectItem>
                      <SelectItem value="lifetime">Lifetime</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {genForm.tier === "lifetime" ? t("admin.no_expiry") : `${t("admin.expires_after")} ${genForm.tier === "tier_1" ? "6" : genForm.tier === "tier_2" ? "12" : "24"} ${t("admin.months_label")}`}
                  </p>
                </div>
                <div><Label>{t("admin.tag")}</Label><Input value={genForm.tag} onChange={e => setGenForm({...genForm, tag: e.target.value})} className="mt-1" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowGenerate(false)}>{t("admin.cancel")}</Button>
                <Button onClick={handleGenerate} disabled={generating} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-generate-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" />{genForm.count} {t("admin.generate_keys")}</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: t("admin.total"), val: stats.total, color: "text-blue-500" },
            { label: t("admin.available"), val: stats.unused, color: "text-emerald-500" },
            { label: t("admin.activated"), val: stats.redeemed, color: "text-amber-500" },
            { label: t("admin.revoked"), val: stats.revoked, color: "text-red-500" },
          ].map((s, i) => (
            <Card key={i} className="bg-card border-border"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      {stats?.tiers && Object.keys(stats.tiers).length > 0 && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(stats.tiers).map(([tier, data]) => (
            <Badge key={tier} variant="outline" className="py-1.5 px-3 text-sm">
              <Package className="w-3 h-3 mr-1.5" />{tier}: {data.total} ({data.unused} {t("admin.available").toLowerCase()})
            </Badge>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Select value={filterStatus} onValueChange={v => { setFilterStatus(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder={t("admin.status_filter")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.all")}</SelectItem>
            <SelectItem value="unused">{t("admin.available")}</SelectItem>
            <SelectItem value="redeemed">{t("admin.activated")}</SelectItem>
            <SelectItem value="revoked">{t("admin.revoked")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterTier} onValueChange={v => { setFilterTier(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder={t("admin.tier_filter")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.all_tiers")}</SelectItem>
            <SelectItem value="tier_1">Tier 1</SelectItem>
            <SelectItem value="tier_2">Tier 2</SelectItem>
            <SelectItem value="tier_3">Tier 3</SelectItem>
            <SelectItem value="lifetime">Lifetime</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="secondary">{total} keys</Badge>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.key")}</TableHead>
                <TableHead>{t("admin.tier")}</TableHead>
                <TableHead>{t("admin.plan")}</TableHead>
                <TableHead>{t("admin.status")}</TableHead>
                <TableHead>{t("admin.user_col")}</TableHead>
                <TableHead>{t("admin.date")}</TableHead>
                <TableHead className="text-right">{t("admin.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{t("admin.no_keys")}</TableCell></TableRow>
              ) : keys.map((k) => (
                <TableRow key={k.id} data-testid={`key-row-${k.id}`}>
                  <TableCell className="font-mono-data text-sm font-medium">{k.code}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{tierLabel(k.tier)}</Badge></TableCell>
                  <TableCell className="text-sm">{k.plan_name}</TableCell>
                  <TableCell><Badge className={statusColor(k.status)}>{k.status}</Badge></TableCell>
                  <TableCell className="text-sm">{k.user ? k.user.email : <span className="text-muted-foreground">-</span>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{k.redeemed_at ? new Date(k.redeemed_at).toLocaleDateString("nl-NL") : k.created_at?.slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {k.status === "unused" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowAssign(k)} title={t("admin.assign")}><UserPlus className="w-3.5 h-3.5" /></Button>
                      )}
                      {k.status === "unused" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={async () => {
                          await api.put(`/appsumo/admin/keys/${k.id}/revoke`);
                          toast.success(t("admin.revoked"));
                          fetchData();
                        }} title={t("admin.revoke")}><Ban className="w-3.5 h-3.5" /></Button>
                      )}
                      {(k.status === "revoked" || k.status === "redeemed") && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={async () => {
                          await api.put(`/appsumo/admin/keys/${k.id}/reactivate`);
                          toast.success(t("admin.reactivate") + " OK");
                          fetchData();
                        }} title={t("admin.reactivate")}><RotateCcw className="w-3.5 h-3.5" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > 30 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t("admin.prev_page")}</Button>
          <span className="text-sm text-muted-foreground py-2">{t("admin.page_of").replace("{page}", page).replace("{total}", Math.ceil(total / 30))}</span>
          <Button variant="outline" size="sm" disabled={keys.length < 30} onClick={() => setPage(p => p + 1)}>{t("admin.next_page")}</Button>
        </div>
      )}

      {showAssign && (
        <Dialog open={!!showAssign} onOpenChange={() => setShowAssign(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("admin.assign_key")}: {showAssign.code}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground">{t("admin.assign_desc")}</p>
              <div><Label>{t("admin.email_address")}</Label><Input value={assignEmail} onChange={e => setAssignEmail(e.target.value)} placeholder="user@email.com" className="mt-1" data-testid="assign-email" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssign(null)}>{t("admin.cancel")}</Button>
              <Button onClick={async () => {
                try {
                  await api.post(`/appsumo/admin/keys/${showAssign.id}/assign`, { email: assignEmail });
                  toast.success(`${t("admin.assign_key")} - ${assignEmail}`);
                  setShowAssign(null);
                  setAssignEmail("");
                  fetchData();
                } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
              }} className="bg-orange-500" data-testid="confirm-assign-btn">{t("admin.assign")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
